import React, { useState, useEffect, useRef, useContext, useCallback } from "react";
import {
  Box,
  Flex,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Button,
  Spinner,
  Text,
  VStack,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  DrawerFooter,
  Grid,
  GridItem,
  SimpleGrid,
  Divider,
  IconButton,
  Tooltip,
  Input,
  Textarea,
  FormControl,
  FormLabel,
  Select,
  Switch,
} from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon, MinusIcon } from "@chakra-ui/icons";
import { AppContext } from "AppProvider/AppContext";
import DynamicFormJson from "./components/PODynamicFormJson";
import DynamicFormTable from "./components/PODynamicTableLabel";
import { POSchemas } from "./poSchemas";
import FileViewer from "components/common/FileViewerPdfAndImage/FileViewer";
import { appRequest } from "routes";
import PoCommentDrawer from "./components/PoCommentDrawer";
import { isEmpty, debounce } from "lodash";

const getPlantCodeFromData = (data: any): string => {
  if (!data || !Array.isArray(data?.values)) return "";
  for (let i = data.values.length - 1; i >= 0; i--) {
    const tables = data.values[i]?.values?.tables;
    if (tables) {
      const poLineItems = tables.PO_Line_Items || tables.PO_line_Items || tables.PO_Line_items || tables.PO_line_items;
      if (Array.isArray(poLineItems) && poLineItems.length > 0) {
        const firstRow = poLineItems[0];
        if (firstRow) {
          const plantVal = firstRow.Plant;
          if (plantVal && typeof plantVal === "object" && !Array.isArray(plantVal)) {
            const val = (plantVal.editFieldValue !== null && plantVal.editFieldValue !== undefined && plantVal.editFieldValue !== "")
              ? plantVal.editFieldValue
              : plantVal.originalValue;
            if (val !== undefined && val !== null && val !== "") return String(val);
          } else if (plantVal !== undefined && plantVal !== null && plantVal !== "") {
            return String(plantVal);
          }
        }
      }
    }
  }
  return "";
};

const getVendorCodeFromData = (data: any): string => {
  if (!data || !Array.isArray(data?.values)) return "";

  // 1. Search root fields first (including Vender_Code with an 'e')
  for (let i = data.values.length - 1; i >= 0; i--) {
    const fields = data.values[i]?.values?.fields;
    if (fields) {
      const vendorVal = fields.Vender_Code || fields.Vendor_Code || fields.PO_Vendor_Code || fields.Vendor_Number || fields.Vendor_Id;
      if (vendorVal) {
        if (typeof vendorVal === "object" && !Array.isArray(vendorVal)) {
          const val = (vendorVal.editFieldValue !== null && vendorVal.editFieldValue !== undefined && vendorVal.editFieldValue !== "")
            ? vendorVal.editFieldValue
            : vendorVal.originalValue;
          if (val !== undefined && val !== null && val !== "") return String(val);
        } else {
          return String(vendorVal);
        }
      }
    }
  }

  // 2. Fallback to table line items
  for (let i = data.values.length - 1; i >= 0; i--) {
    const tables = data.values[i]?.values?.tables;
    if (tables) {
      const lineItems = tables.Invoice_line_Items || tables.Invoice_Line_Items || tables.PO_Line_Items || tables.PO_line_Items || tables.PO_Line_items || tables.PO_line_items;
      if (Array.isArray(lineItems) && lineItems.length > 0) {
        const firstRow = lineItems[0];
        if (firstRow) {
          const vendorVal = firstRow.Vendor_Code || firstRow.Vender_Code || firstRow.ZCL_Vendor_Code || firstRow.ZCLI_Vendor_Code;
          if (vendorVal && typeof vendorVal === "object" && !Array.isArray(vendorVal)) {
            const val = (vendorVal.editFieldValue !== null && vendorVal.editFieldValue !== undefined && vendorVal.editFieldValue !== "")
              ? vendorVal.editFieldValue
              : vendorVal.originalValue;
            if (val !== undefined && val !== null && val !== "") return String(val);
          } else if (vendorVal !== undefined && vendorVal !== null && vendorVal !== "") {
            return String(vendorVal);
          }
        }
      }
    }
  }
  return "";
};

interface POComponentProps {
  data: any;
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (finalData: any, status?: string) => void;
  isLoading?: boolean;
  file?: any;
  handleGet?:any;
  name?: string;
}

export default function POComponent({ handleGet, data, isOpen, onClose, onSubmit, isLoading, file, name }: POComponentProps) {
  const { toastNotify, workflowProcess, accountDetails } = useContext(AppContext);
  const [schema, setSchema] = useState<any>({ fields: {}, tables: [] });
  const [initialValues, setInitialValues] = useState<any>({ fields: {}, tables: [] });
  const [mergedData, setMergedData] = useState<any>({});
  const [finalFieldValues, setFinalFieldValues] = useState<any>({});
  const [tableErrors, setTableErrors] = useState<any>({});
  const [lineItems, setLineItems] = useState<any[]>([]);
  const [isFullWidthForm, setIsFullWidthForm] = useState(false);
  const [financeFormData, setFinanceFormData] = useState<any>({
    headerText: "",
    gstPartner: "",
    businessPlace: null,
    companyCode: "",
    invoiceType: "",
    isTdsApplicable: false,
    tdsSections: [{ tdsTaxCode: "", tdsBasicAmount: "" }],
  });
  const [tdsTaxCodeOptions, setTdsTaxCodeOptions] = useState<any[]>([]);
  const [businessPlaceOptions, setBusinessPlaceOptions] = useState<any[]>([]);
  const [companyCodeOptions, setCompanyCodeOptions] = useState<any[]>([]);
  const [formFields, setFormFields] = useState<any>({});

  const debouncedSetFormFields = useCallback(
    debounce((values: any) => {
      setFormFields(values);
    }, 500),
    []
  );

  useEffect(() => {
    return () => {
      debouncedSetFormFields.cancel();
    };
  }, [debouncedSetFormFields]);
  const hasFetchedCompanyCodes = useRef(false);
  const prevCompanyCode = useRef<string | null>(null);
  const lastFetchedFinanceCompanyCode = useRef<string | null>(null);

  const extractValue = (cell: any) => {
    if (cell && typeof cell === "object" && !Array.isArray(cell) && (cell.hasOwnProperty("originalValue") || cell.hasOwnProperty("editFieldValue"))) {
      return (cell.editFieldValue !== null && cell.editFieldValue !== undefined && cell.editFieldValue !== "") ? cell.editFieldValue : cell.originalValue;
    }
    return cell;
  };
  const [commentModel, setCommentModel] = useState({
    open: false,
    selectedStatus: "approved",
    comment: "",
  });

  const STAGES = ["extraction", "po", "validation", "miro"];
  const poExtractionType = data?.poExtractionType || "extraction";
  const processType = data?.processType || "Po";

  const [selectedStage, setSelectedStage] = useState<string>(poExtractionType);

  useEffect(() => {
    setSelectedStage(poExtractionType);
  }, [poExtractionType]);

  useEffect(() => {
    hasFetchedCompanyCodes.current = false;
    prevCompanyCode.current = null;
    lastFetchedFinanceCompanyCode.current = null;
  }, [data?._id]);

  const isPendingApproval = !name || name === "Pending Approval";
  const isAdmin = accountDetails?.level?.toLowerCase() === "admin";
  const isStageEditable = isPendingApproval && !isAdmin && (selectedStage.toLowerCase() === poExtractionType.toLowerCase());

  const finalTableDataRef = useRef<Record<string, any>>({});

  const [fetchFileLoader, setFetchFileLoader] = useState<boolean>(false);
  const [sample, setSample] = useState<any>({
    url: "",
    file: null,
    type: null,
    buffer: null,
  });

  useEffect(() => {
    if (isOpen && file) {
      getFile();
    }
  }, [isOpen, file]);

  const getFile = async () => {
    try {
      if (file) {
        setFetchFileLoader(true);
        const response: any = await appRequest("filesystem", "get", {
          id: file._id,
        });
        if (response && !response?.error) {
          let buffer = typeof response === "string" ? JSON.parse(response) : response;
          buffer = new Uint8Array(buffer);

          const fileURL = URL.createObjectURL(
            new Blob([buffer], { type: file.metadata?.contentType || "application/pdf" })
          );
          setSample({
            url: fileURL,
            file: file,
            type: file.metadata?.contentType || "application/pdf",
            buffer: buffer,
          });
        }
      }
    } catch (error: any) {
      console.error(error);
    } finally {
      setFetchFileLoader(false);
    }
  };

  const formRef = useRef<any>();
  const tableRefs = useRef<any[]>([]);

  // Determine stage based on data

  useEffect(() => {
    const fetchCompanyCodes = async () => {
      const buyerName = formFields?.Buyer_Name || extractValue(mergedData?.Buyer_Name);
      const buyerGstIn = formFields?.Buyer_GSTIN_OR_Tax_Identification_Number || extractValue(mergedData?.Buyer_GSTIN_OR_Tax_Identification_Number);

      if (!buyerName && !buyerGstIn) return;

      const fetchKey = `${buyerName}-${buyerGstIn}`;
      if (hasFetchedCompanyCodes.current && prevCompanyCode.current === fetchKey) {
        return;
      }

      try {
        const payload = {
          buyerName,
          buyerGstin: buyerGstIn,
          type: "getCompanyCode",
          ...workflowProcess,
        };
        const response: any = await appRequest(
          "user",
          "masterFinancialApi",
          payload
        );
        if (response?.status === "success" && Array.isArray(response?.data)) {
          const options = response.data.map((item: any) => {
            if (typeof item === "object" && item !== null) {
              const val = item.companyCode || item.value || item.label || "";
              const lbl = item.label || item.companyCode || item.value || "";
              return { label: String(lbl), value: String(val) };
            }
            return { label: String(item), value: String(item) };
          }) || [];

          setCompanyCodeOptions(options);
          hasFetchedCompanyCodes.current = true;
          prevCompanyCode.current = fetchKey;

          // Auto-select the first option if none is currently selected in financeFormData,
          // or if the current selected companyCode is no longer valid in the newly fetched options list.
          const hasOption = options.some((opt: any) => opt.value === financeFormData.companyCode);
          if (options.length > 0 && (!financeFormData.companyCode || !hasOption)) {
            setFinanceFormData((prev: any) => ({
              ...prev,
              companyCode: options[0].value,
            }));
          }
        }
      } catch (error) {
        console.error("Failed to load company codes", error);
      }
    };

    if (selectedStage.toLowerCase() === "miro") {
      fetchCompanyCodes();
    }
  }, [
    mergedData,
    selectedStage,
    workflowProcess,
    formFields?.Buyer_Name,
    formFields?.Buyer_GSTIN_OR_Tax_Identification_Number
  ]);

  useEffect(() => {
    const fetchFinanceOptions = async () => {
      const codeToUse = financeFormData.companyCode;
      if (!codeToUse) return;

      const plantCode = getPlantCodeFromData(data);
      const vendorCode = getVendorCodeFromData(data);
      const cacheKey = `${codeToUse}-${plantCode}-${vendorCode}`;

      if (lastFetchedFinanceCompanyCode.current === cacheKey) {
        return;
      }

      try {
        // Fetch TDS Tax Code
        const tdsPayload = {
          type: "getTdsTaxCode",
          companyCode: codeToUse,
          vendorCode: vendorCode,
          processName: processType
        };
        const tdsRes: any = await appRequest("user", "masterFinancialApi", tdsPayload);
        if (tdsRes?.status === "success" && Array.isArray(tdsRes?.data)) {
          setTdsTaxCodeOptions(tdsRes.data.map((code: any) => {
            if (typeof code === "object" && code !== null) {
              const val = code.tdsTaxCode || code.value || code.label || "";
              const lbl = code.label || code.tdsTaxCode || code.value || "";
              return { label: String(lbl), value: String(val), taxType: code.taxType || "" };
            }
            return { label: String(code), value: String(code) };
          }));
        }

        // Fetch Business Place
        const bpPayload = {
          type: "getBusinessPlace",
          companyCode: codeToUse,
          plantCode: plantCode,
          processName: processType
        };
        const bpRes: any = await appRequest("user", "masterFinancialApi", bpPayload);
        if (bpRes?.status === "success" && Array.isArray(bpRes?.data)) {
          setBusinessPlaceOptions(bpRes.data.map((bp: any) => {
            if (typeof bp === "object" && bp !== null) {
              const val = bp.businessPlace || bp.value || bp.label || bp.plantCode || "";
              const lbl = bp.label || bp.businessPlace || bp.value || bp.plantCode || "";
              return { label: String(lbl), value: String(val) };
            }
            return { label: String(bp), value: String(bp) };
          }));
        }

        lastFetchedFinanceCompanyCode.current = cacheKey;
      } catch (error) {
        console.error("Failed to load finance options", error);
      }
    };

    if (selectedStage.toLowerCase() === "miro" && financeFormData.companyCode) {
      fetchFinanceOptions();
    }
  }, [selectedStage, processType, financeFormData.companyCode, data]);

  useEffect(() => {
    if (data && processType === "Po") {
      extractSchemaAndValues();
    }
  }, [data, selectedStage]);

  const extractSchemaAndValues = () => {
    try {
      // Extract schema from static frontend file instead of workflow data
      const workflowSchema = POSchemas[selectedStage.toLowerCase()];
      if (workflowSchema) {
        setSchema({
          fields: workflowSchema.fields || {},
          tables: workflowSchema.tables || [],
        });
      } else {
        console.warn(`No static schema found for selectedStage: ${selectedStage}`);
        setSchema({ fields: {}, tables: [] });
      }

      // Extract values
      let targetEntry = null;
      if (data?.values && Array.isArray(data.values)) {
        // Find entry for selectedStage
        for (let i = data.values.length - 1; i >= 0; i--) {
          const entry = data.values[i];
          if (entry?.poProcessTypeValue?.toLowerCase() === selectedStage.toLowerCase()) {
            targetEntry = entry;
            break;
          }
        }
        // Fallback for extraction
        if (!targetEntry && selectedStage.toLowerCase() === "extraction") {
          targetEntry = data.values.find((e: any) => e.level === "admin") || data.values[0];
        }
        // Fallback for the active stage (if no matching poProcessTypeValue yet)
        if (!targetEntry && selectedStage.toLowerCase() === poExtractionType.toLowerCase()) {
          targetEntry = data.values[data.values.length - 1];
        }
      }

      const currentValues = targetEntry?.values || { fields: {}, tables: {} };

      // Separate original/edit format for fields
      let fieldsInitial: Record<string, any> = {};
      let originalFieldsData = currentValues.fields || {};

      Object.keys(originalFieldsData).forEach((key) => {
        const field = originalFieldsData[key];
        fieldsInitial[key] = extractValue(field);
      });

      const flattenTableRows = (rows: any[]) => {
        return rows.map(row => {
          const flatRow: any = {};
          Object.keys(row).forEach(key => {
            flatRow[key] = extractValue(row[key]);
          });
          return flatRow;
        });
      };

      // Parse tables
      let tablesInitial: any[] = [];
      if (currentValues.tables && typeof currentValues.tables === "object") {
         Object.keys(currentValues.tables).forEach((tableName) => {
             tablesInitial.push({
                 [tableName]: currentValues.tables[tableName]
             })
         });
      }

      setInitialValues({
        fields: fieldsInitial,
        tables: tablesInitial,
      });

      if (currentValues.financeForm) {
        const bpVal = currentValues.financeForm.businessPlace;
        const normalizedBp = (bpVal && typeof bpVal === "object")
          ? (bpVal.value || bpVal.businessPlace || bpVal.label || "")
          : bpVal;

        const ccVal = currentValues.financeForm.companyCode;
        const normalizedCc = (ccVal && typeof ccVal === "object")
          ? (ccVal.value || ccVal.companyCode || ccVal.label || "")
          : ccVal;

        const rawTdsSections = currentValues.financeForm.tdsSections || [
          {
            tdsTaxCode: currentValues.financeForm.tdsTaxCode || "",
            tdsBasicAmount: currentValues.financeForm.tdsBasicAmount || "",
          }
        ];

        const normalizedTdsSections = rawTdsSections.map((sec: any) => {
          const tc = sec.tdsTaxCode;
          const normalizedTc = (tc && typeof tc === "object")
            ? (tc.value || tc.tdsTaxCode || tc.label || "")
            : tc;
          return {
            ...sec,
            tdsTaxCode: normalizedTc || "",
            tdsBasicAmount: sec.tdsBasicAmount || "",
          };
        });

        setFinanceFormData({
          headerText: currentValues.financeForm.headerText || "",
          gstPartner: currentValues.financeForm.gstPartner || "",
          businessPlace: normalizedBp || "",
          companyCode: normalizedCc || "",
          invoiceType: currentValues.financeForm.invoiceType || "",
          isTdsApplicable: currentValues.financeForm.isTdsApplicable || false,
          tdsSections: normalizedTdsSections,
        });
      } else {
        setFinanceFormData({
          headerText: "",
          gstPartner: "",
          businessPlace: "",
          companyCode: "",
          invoiceType: "",
          isTdsApplicable: false,
          tdsSections: [{ tdsTaxCode: "", tdsBasicAmount: "" }]
        });
      }

      setMergedData(originalFieldsData);

      // Initialize line items if Invoice_line_Items, Invoice_Line_Items or PO_Line_Items exists
      if (currentValues.tables?.Invoice_line_Items) {
        setLineItems(flattenTableRows(currentValues.tables.Invoice_line_Items));
      } else if (currentValues.tables?.Invoice_Line_Items) {
        setLineItems(flattenTableRows(currentValues.tables.Invoice_Line_Items));
      } else if (currentValues.tables?.PO_Line_Items) {
        setLineItems(flattenTableRows(currentValues.tables.PO_Line_Items));
      } else {
        setLineItems([]);
      }

    } catch (e) {
      console.error("Error extracting schema and values", e);
    }
  };

  const handleTabChange = () => {
    // Save table data on tab change
    if (tableRefs.current) {
      tableRefs.current.forEach((tableRef) => {
        if (tableRef && tableRef.handleSave) {
          tableRef.handleSave();
        }
      });
    }
  };

  const getTrueOriginalValue = (val: any): any => {
    if (val && typeof val === "object" && !Array.isArray(val) && ("originalValue" in val || "editFieldValue" in val)) {
      return getTrueOriginalValue(val.originalValue);
    }
    return val;
  };

  const getFinalData = () => {
    const fieldValues = formRef?.current?.values || {};
    const finalData: { fields: Record<string, any>, tables: Record<string, any>, financeForm?: Record<string, any> } = {
        fields: {},
        tables: {}
    };

    // Construct final fields
    Object.keys(schema.fields).forEach(key => {
        let original = getTrueOriginalValue(mergedData[key]);
        if (original === undefined || original === null) original = "";

        const edited = fieldValues[key] !== undefined ? fieldValues[key] : null;
        finalData.fields[key] = {
            originalValue: original,
            editFieldValue: original !== edited ? edited : null
        };
    });

    // Add table data
    finalData.tables = { ...finalTableDataRef.current };

    // Add Finance Form data if we are in miro stage
    if (poExtractionType.toLowerCase() === "miro") {
      const firstSection = financeFormData.tdsSections?.[0] || {};
      finalData.financeForm = {
        ...financeFormData,
        tdsTaxCode: firstSection.tdsTaxCode || "",
        tdsBasicAmount: firstSection.tdsBasicAmount || "",
        taxType: firstSection.taxType || "",
      };
    }

    return finalData;
  };

  const handleValidateAndSubmit = async (status: string) => {
    let hasTableErrors = false;

    if (tableRefs && tableRefs?.current) {
      tableRefs.current.forEach((tableRef, index) => {
        if (tableRef && tableRef.handleSave) {
          const result = tableRef.handleSave();
          if (result && result.errors) {
            // newErrors has the structure { rowIndex: { fieldName: errorMessage } }
            // So we need to check if any of the row error objects actually have keys
            const hasActualErrors = Object.values(result.errors).some(
              (rowErrors: any) => rowErrors && Object.keys(rowErrors).length > 0
            );
            if (hasActualErrors) {
              hasTableErrors = true;
            }
          }
          if (result && result.data) {
             const tableName = Object.keys(schema.tables[index])[0];
             finalTableDataRef.current[tableName] = result.data;
          }
        }
      });
    }

    if (formRef.current) {
      formRef.current.submitForm();
      const errors = await formRef.current.validateForm();

      if (errors && Object.keys(errors).length > 0) {
        const message = Object.entries(errors)
          .map(([key, value]) => `${key.replace(/_/g, " ")}: ${value}`)
          .join("\n");

        toastNotify({
          title: "Validation Error",
          message,
          type: "info",
        });
        return;
      }

      if (Object.keys(errors).length === 0 && !hasTableErrors) {
        setCommentModel({
          open: true,
          selectedStatus: status,
          comment: "",
        });
      } else {
        toastNotify({
          title: "Validation Error",
          message: "Please fix the errors in the table.",
          type: "info",
        });
      }
    }
  };

  if (isLoading) {
    return (
      <Flex minH="40vh" justify="center" align="center">
        <VStack spacing={4}>
          <Spinner thickness="4px" speed="0.65s" emptyColor="gray.200" color="teal.500" size="xl" />
          <Text fontSize="lg" color="gray.600">Loading PO Data...</Text>
        </VStack>
      </Flex>
    );
  }



  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="full">
      <DrawerOverlay />
      <DrawerContent maxW="95vw">
        <DrawerCloseButton mt={1} />
        <DrawerHeader borderBottomWidth="1px" pb={2} bg="white">
          <Flex justify="space-between" align="center" mb={0}>
            <Text fontSize="xl" fontWeight="bold" color="teal.600" textTransform="capitalize">
              Purchase Order Workflow
            </Text>
          </Flex>
        </DrawerHeader>

        <DrawerBody height="85vh" px={{ base: 4, lg: 6 }} py={2} bg="gray.50">
          <Grid
            templateColumns={{ lg: file ? (isFullWidthForm ? "0fr 1fr" : "1fr 1.5fr") : "1fr" }}
            transition="grid-template-columns 0.4s ease-in-out"
            columnGap={{ lg: isFullWidthForm ? 0 : 5 }}
            rowGap={2}
            h="100%"
          >
            {file && (
              <Box
                h="100%"
                minW={0}
                position="relative"
                overflow="hidden"
                w="100%"
                bg="white"
                borderRadius={8}
                border="2px solid"
                borderColor="gray.200"
              >
                {fetchFileLoader ? (
                  <Flex h="100%" justify="center" align="center">
                    <Spinner size="xl" thickness="4px" speed="0.65s" color="teal.400" />
                  </Flex>
                ) : (
                  <FileViewer sample={sample} />
                )}
              </Box>
            )}

            <Box minW={0} w="100%" h="100%" border="2px solid" borderColor="teal.400" borderRadius={8} p={4} bg="white" position="relative">
              {file && (
                <Tooltip label={isFullWidthForm ? "Collapse Form" : "Expand Form"} placement="left">
                  <IconButton
                    aria-label="Toggle Full Width"
                    icon={isFullWidthForm ? <ChevronRightIcon boxSize={6} /> : <ChevronLeftIcon boxSize={6} />}
                    position="absolute"
                    top="22px"
                    right={6}
                    zIndex={2}
                    onClick={() => setIsFullWidthForm(!isFullWidthForm)}
                    size="sm"
                    colorScheme="teal"
                    variant="outline"
                    borderRadius="full"
                    boxShadow="sm"
                    bg="white"
                    _hover={{ bg: "teal.50" }}
                  />
                </Tooltip>
              )}

              {/* Main Workflow Stages Tabs */}
      <Tabs index={STAGES.indexOf(selectedStage.toLowerCase())} onChange={(index) => setSelectedStage(STAGES[index])} isLazy variant="enclosed" colorScheme="teal" h="100%" display="flex" flexDirection="column">
        <TabList mb="1em">
          <Tab isDisabled={poExtractionType.toLowerCase() !== "extraction" && !data?.values?.some((e: any) => e.poProcessTypeValue?.toLowerCase() === "extraction" || e.level === "admin")}>Extraction</Tab>
          <Tab isDisabled={poExtractionType.toLowerCase() !== "po" && !data?.values?.some((e: any) => e.poProcessTypeValue?.toLowerCase() === "po")}>PO</Tab>
          <Tab isDisabled={poExtractionType.toLowerCase() !== "validation" && !data?.values?.some((e: any) => e.poProcessTypeValue?.toLowerCase() === "validation")}>Validation</Tab>
          <Tab isDisabled={poExtractionType.toLowerCase() !== "miro" && !data?.values?.some((e: any) => e.poProcessTypeValue?.toLowerCase() === "miro")}>Miro</Tab>
        </TabList>

        <TabPanels>
          {STAGES.map((stage) => (
            <TabPanel key={stage} p={0}>
              {/* Only render the inner content if this is the currently selected stage */}
              {selectedStage.toLowerCase() === stage && (
                <Tabs onChange={handleTabChange} colorScheme="blue">
                  <TabList>
                    <Tab>Fields</Tab>
                    {schema.tables.map((table: any, index: number) => {
                      const tableName = Object.keys(table)[0];
                      return <Tab key={index}>{tableName.split("_").join(" ")}</Tab>;
                    })}
                    {stage === "miro" && <Tab>Finance Form</Tab>}
                  </TabList>

                  <TabPanels>
                    <TabPanel px={0}>
                       <Box maxH="65vh" overflowY="auto" className="customScrollBar">
                         {Object.keys(schema.fields).length > 0 && (
                           <DynamicFormJson
                             formRef={formRef}
                             schema={{ fields: schema.fields }}
                             initialValuess={initialValues.fields}
                             originalFields={mergedData}
                             isDisabled={!isStageEditable}
                             finalFieldValues={finalFieldValues}
                             onFieldsChange={debouncedSetFormFields}
                             onSubmit={({ values, setSubmitting }: any) => {
                               setSubmitting(false);
                               // Logic for field submission
                             }}
                           />
                         )}
                       </Box>
                    </TabPanel>

                    {schema.tables.map((tableItem: any, index: number) => {
                      const tableName = Object.keys(tableItem)[0];
                      const tableSchema = tableItem[tableName];
                      const tableDataEntry = initialValues.tables.find(
                        (entry: any) => Object.keys(entry)[0] === tableName
                      );
                      const tableData = tableDataEntry ? tableDataEntry[tableName] : [];

                      return (
                        <TabPanel key={tableName} px={0}>
                          <DynamicFormTable
                            schema={tableSchema || {}}
                            tableData={tableData || []}
                            lineItems={lineItems}
                            setLineItems={setLineItems}
                            submittedData={data?.values || []}
                            tableName={tableName}
                            onSubmit={(values: any) => {
                               // Table save logic
                            }}
                            ref={(el: any) => (tableRefs.current[index] = el)}
                            errors={tableErrors[tableName] || {}}
                            setErrors={(newErrors: any) => setTableErrors((prev: any) => ({ ...prev, [tableName]: newErrors }))}
                            isDisabled={!isStageEditable}
                          />
                        </TabPanel>
                      );
                    })}

                    {stage === "miro" && (
                      <TabPanel px={0}>
                        <Box maxH="65vh" overflowY="auto" p={6} bg="white" borderRadius="lg" borderWidth="1px" borderColor="gray.200" boxShadow="sm" className="customScrollBar">
                          <VStack spacing={8} align="stretch">
                            {/* Section 1: General Finance Info */}
                            <Box>
                              <Text fontSize="md" fontWeight="bold" color="teal.600" mb={4}>
                                General Finance Information
                              </Text>
                              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                                <GridItem colSpan={{ base: 1, md: 3 }}>
                                  <FormControl>
                                    <FormLabel fontWeight="semibold" color="gray.700">Header Text</FormLabel>
                                    <Textarea
                                      value={financeFormData.headerText}
                                      onChange={(e) => setFinanceFormData({...financeFormData, headerText: e.target.value})}
                                      placeholder="Enter Header Text"
                                      resize="vertical"
                                      focusBorderColor="teal.500"
                                      borderRadius="md"
                                      isDisabled={!isStageEditable}
                                    />
                                  </FormControl>
                                </GridItem>
                                <FormControl>
                                  <FormLabel fontWeight="semibold" color="gray.700">GST Partner</FormLabel>
                                  <Input
                                    value={financeFormData.gstPartner}
                                    onChange={(e) => setFinanceFormData({...financeFormData, gstPartner: e.target.value})}
                                    placeholder="Enter GST Partner"
                                    focusBorderColor="teal.500"
                                    borderRadius="md"
                                    isDisabled={!isStageEditable}
                                  />
                                </FormControl>
                                <FormControl>
                                  <FormLabel fontWeight="semibold" color="gray.700">Company Code</FormLabel>
                                  <Select
                                    value={financeFormData.companyCode || ""}
                                    onChange={(e) => setFinanceFormData({...financeFormData, companyCode: e.target.value})}
                                    placeholder="Select Company Code"
                                    focusBorderColor="teal.500"
                                    borderRadius="md"
                                    isDisabled={!isStageEditable}
                                  >
                                    {companyCodeOptions.map((opt, i) => (
                                      <option key={i} value={opt.value}>{opt.label}</option>
                                    ))}
                                  </Select>
                                </FormControl>
                                <FormControl>
                                  <FormLabel fontWeight="semibold" color="gray.700">Business Place</FormLabel>
                                  <Select
                                    value={financeFormData.businessPlace || ""}
                                    onChange={(e) => setFinanceFormData({...financeFormData, businessPlace: e.target.value})}
                                    placeholder="Select Business Place"
                                    focusBorderColor="teal.500"
                                    borderRadius="md"
                                    isDisabled={!isStageEditable}
                                  >
                                    {businessPlaceOptions.map((opt, i) => (
                                      <option key={i} value={opt.value}>{opt.label}</option>
                                    ))}
                                  </Select>
                                </FormControl>
                                <FormControl>
                                  <FormLabel fontWeight="semibold" color="gray.700">Invoice Type</FormLabel>
                                  <Select
                                    value={financeFormData.invoiceType || ""}
                                    onChange={(e) => setFinanceFormData({...financeFormData, invoiceType: e.target.value})}
                                    placeholder="Select Invoice Type"
                                    focusBorderColor="teal.500"
                                    borderRadius="md"
                                    isDisabled={!isStageEditable}
                                  >
                                     <option value="AA">AA - Asset posting</option>
  <option value="M1">M1 - EOU RM/PM Ind Inv</option>
  <option value="M9">M9 - EOU Service GRIR In</option>
  <option value="MA">MA - RM/PM Indig Invoice</option>
  <option value="MB">MB - RM/PM Imported Inv</option>
  <option value="MF">MF - FG P - P Invoice</option>
  <option value="MH">MH - Promotional Items Inv</option>
  <option value="MI">MI - Service GRIR Invoice</option>
  <option value="MN">MN - RM/PM Indig Credit</option>
                                  </Select>
                                </FormControl>
                                <GridItem colSpan={{ base: 1, md: 3 }}>
                                  <FormControl display="flex" alignItems="center">
                                    <FormLabel htmlFor="tds-applicable" mb="0" fontWeight="semibold" color="gray.700">
                                      Is TDS Applicable?
                                    </FormLabel>
                                    <Switch
                                      id="tds-applicable"
                                      isChecked={financeFormData.isTdsApplicable}
                                      onChange={(e) => setFinanceFormData({...financeFormData, isTdsApplicable: e.target.checked})}
                                      colorScheme="teal"
                                      isDisabled={!isStageEditable}
                                    />
                                  </FormControl>
                                </GridItem>
                              </SimpleGrid>
                            </Box>

                            <Divider />

                            {/* Section 2: TDS Tax Sections */}
                            {financeFormData.isTdsApplicable && (
                              <Box bg="gray.50" p={5} borderRadius="lg" border="1px solid" borderColor="gray.200">
                                <Flex justify="space-between" align="center" mb={4}>
                                  <Text fontSize="md" fontWeight="bold" color="teal.600">
                                    TDS Tax Sections
                                  </Text>
                                  {isStageEditable && (
                                    <Button
                                      size="sm"
                                      colorScheme="teal"
                                      variant="solid"
                                      onClick={() => {
                                        const updatedSections = [...(financeFormData.tdsSections || []), { tdsTaxCode: "", tdsBasicAmount: "" }];
                                        setFinanceFormData({ ...financeFormData, tdsSections: updatedSections });
                                      }}
                                      _hover={{ transform: "translateY(-1px)", boxShadow: "sm" }}
                                      _active={{ transform: "translateY(0)" }}
                                    >
                                      + Add Tax Code
                                    </Button>
                                  )}
                                </Flex>

                                <VStack spacing={4} align="stretch">
                                  {(financeFormData.tdsSections || []).map((section: any, idx: number) => (
                                    <Flex
                                      key={idx}
                                      gap={4}
                                      align="flex-end"
                                      w="100%"
                                      bg="white"
                                      p={4}
                                      borderRadius="md"
                                      border="1px solid"
                                      borderColor="gray.100"
                                      boxShadow="xs"
                                      transition="all 0.2s"
                                      _hover={{ boxShadow: "sm" }}
                                    >
                                      <FormControl flex={1}>
                                        <FormLabel fontWeight="semibold" color="gray.600" fontSize="xs" textTransform="uppercase" letterSpacing="wide">
                                          TDS Tax Code {idx + 1}
                                        </FormLabel>
                                        <Select
                                          value={section.tdsTaxCode || ""}
                                          onChange={(e) => {
                                            const selectedVal = e.target.value;
                                            const selectedOpt = tdsTaxCodeOptions.find((opt: any) => opt.value === selectedVal);
                                            const updated = [...(financeFormData.tdsSections || [])];
                                            updated[idx] = {
                                              ...updated[idx],
                                              tdsTaxCode: selectedVal,
                                              taxType: selectedOpt?.taxType || ""
                                            };
                                            setFinanceFormData({ ...financeFormData, tdsSections: updated });
                                          }}
                                          placeholder="Select TDS Tax Code"
                                          focusBorderColor="teal.500"
                                          isDisabled={!isStageEditable}
                                          size="md"
                                          borderRadius="md"
                                        >
                                          {tdsTaxCodeOptions.map((opt, i) => (
                                            <option key={i} value={opt.value}>{opt.label}</option>
                                          ))}
                                        </Select>
                                      </FormControl>

                                      <FormControl flex={1}>
                                        <FormLabel fontWeight="semibold" color="gray.600" fontSize="xs" textTransform="uppercase" letterSpacing="wide">
                                          Basic Amount {idx + 1}
                                        </FormLabel>
                                        <Input
                                          type="number"
                                          value={section.tdsBasicAmount}
                                          onChange={(e) => {
                                            const updated = [...(financeFormData.tdsSections || [])];
                                            updated[idx] = { ...updated[idx], tdsBasicAmount: e.target.value };
                                            setFinanceFormData({ ...financeFormData, tdsSections: updated });
                                          }}
                                          placeholder="Enter Basic Amount"
                                          focusBorderColor="teal.500"
                                          isDisabled={!isStageEditable}
                                          size="md"
                                          borderRadius="md"
                                        />
                                      </FormControl>

                                      {isStageEditable && (financeFormData.tdsSections || []).length > 1 && (
                                        <IconButton
                                          aria-label="Remove Section"
                                          icon={<MinusIcon />}
                                          size="md"
                                          colorScheme="red"
                                          variant="ghost"
                                          borderRadius="md"
                                          onClick={() => {
                                            const updated = (financeFormData.tdsSections || []).filter((_: any, sIdx: number) => sIdx !== idx);
                                            setFinanceFormData({ ...financeFormData, tdsSections: updated });
                                          }}
                                          _hover={{ bg: "red.50", color: "red.500" }}
                                        />
                                      )}
                                    </Flex>
                                  ))}
                                </VStack>
                              </Box>
                            )}
                          </VStack>
                        </Box>
                      </TabPanel>
                    )}
                  </TabPanels>
                </Tabs>
              )}
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
            </Box>
          </Grid>
        </DrawerBody>
        {isStageEditable && (
          <DrawerFooter borderTopWidth="1px" bg="white" py={4}>
            <Flex gap={4} w="100%" justify="flex-end">
              <Button
                colorScheme="green"
                w="120px"
                h="40px"
                fontWeight="semibold"
                borderRadius="md"
                boxShadow="sm"
                transition="all 0.2s"
                _hover={{ transform: "translateY(-1px)", boxShadow: "md" }}
                _active={{ transform: "translateY(0)" }}
                onClick={() => handleValidateAndSubmit("approved")}
              >
                Approve
              </Button>
              <Button
                colorScheme="red"
                w="120px"
                h="40px"
                fontWeight="semibold"
                borderRadius="md"
                boxShadow="sm"
                transition="all 0.2s"
                _hover={{ transform: "translateY(-1px)", boxShadow: "md" }}
                _active={{ transform: "translateY(0)" }}
                onClick={() => handleValidateAndSubmit("rejected")}
              >
                Reject
              </Button>
            </Flex>
          </DrawerFooter>
        )}
      </DrawerContent>
      <PoCommentDrawer
        commentModel={commentModel}
        setCommentModel={setCommentModel}
        isOpen={commentModel.open}
        handleClose={onClose}
        view={true}
        editedValues={getFinalData()}
        selectedRows={[data]}
        setSelectedRows={() => {}}
        handleGet={handleGet}
        selectedFile={file}
        isSchema={true}
        poProcessTypeValue={poExtractionType}
      />
    </Drawer>
  );
}
